<template>
  <!--
   FIXME: Enable when we fix the impact of intersection observer on the video dimensions.
          The video container ref does not exist which causes dimensions to be 0x0 and
          the video renders with default dimensions which are wrong.
    -->
  <!-- <Intersect v-editable="blok" :threshold="0.5" @enter.once="handleVisibility"> -->
  <div
    v-if="computedVideo"
    class="relative inset-0 flex flex-col items-center justify-end"
    :class="[
      { 'w-full': isFullWidth, '!mt-0': overrideMarginTop },
      marginClasses,
    ]"
  >
    <div v-if="showLink" class="size-full max-w-full overflow-hidden">
      <CMSLink
        :cms-link="blok.link"
        :product-id="blok.product_id"
        :order-number="blok.order_number"
        :aria-label="ctaLinkAriaLabel"
        class="group flex size-full items-center justify-center"
        @click="handleClick"
      >
        <VimeoVideo :blok="computedVideo" />
      </CMSLink>
    </div>
    <VimeoVideo v-else :blok="computedVideo" />
    <div
      v-if="!buttonInvisible"
      class="absolute bottom-10 z-20 flex w-1/2 flex-col items-center justify-between"
    >
      <h2
        :class="[
          'mb-10 text-center font-medium text-white',
          'text-lg leading-[32px]',
          '[@media(min-width:1520px)]:text-lg [@media(min-width:1520px)]:leading-[36px]',
          '[@media(min-width:1680px)]:text-xl [@media(min-width:1680px)]:leading-[40px]',
          '[@media(min-width:1800px)]:text-2xl [@media(min-width:1800px)]:leading-[46px]',
        ]"
      >
        {{ blok.headline }}
      </h2>
      <CtaButton v-if="ctaLink" :blok="ctaLink" @click="handleClick" />
    </div>
  </div>
  <!-- </Intersect> -->
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import type { PropType } from 'vue'
import useUiState from '~/composables/ui/useUiState'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import type { SbInteractableVideo } from '~/cms/storyblok/types/storyblok.d.ts'
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import locales from '~/config/locales'
import { CTA_PROMOTION_CONTEXT_KEY } from '#layers/base/constants/injectionKeys'

const props = defineProps({
  blok: {
    type: Object as PropType<SbInteractableVideo>,
    default: () => {},
  },
  promotionName: {
    type: String,
    default: 'interactable_video',
  },
  isFullWidth: {
    type: Boolean,
    default: true,
  },
  optimiseDesktop: {
    type: Boolean,
    default: true,
  },
  enablePromotionTracking: {
    type: Boolean,
    default: true,
  },
  overrideMarginTop: {
    type: Boolean,
    default: false,
  },
  affectCtaButtonPositioning: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click'])

const { trackPromotionSelect } = useTrackingEvents()
const { isFlyoutMenuOpen } = useUiState()
const { marginClasses } = useStoryblokMargins(props.blok)
const { t } = useI18n()

// Claim the nested CTA's promotion events only while this block is the one
// emitting them, so a consumer that disables tracking here lets the CTA use its
// own parameters instead (ECOM-2766).
if (props.enablePromotionTracking) {
  provide(CTA_PROMOTION_CONTEXT_KEY, { claimedBy: 'interactablevideo' })
}

const computedVideo = computed(() => props.blok.vimeo_video?.[0])

const ctaLink = computed(() =>
  (props.blok.CtaButton || []).length > 0
    ? props.blok.CtaButton?.[0]
    : undefined,
)

const ctaLinkAriaLabel = computed(() => {
  const label = props.blok.headline || computedVideo.value?.altDescription
  return getTargetBlankLabel(label || '', t)
})

const buttonInvisible = computed(() => {
  const button = props.blok.CtaButton?.[0]
  return !button || !!button.invisible
})

const handleClick = (event: MouseEvent) => {
  if (props.enablePromotionTracking) {
    trackPromotionSelect({
      id: props.blok.promotion_id || 'interactable_video_promotion_id',
      name: props.blok.promotion_name || 'interactable_video_promotion_name',
      pageType: isFlyoutMenuOpen.value
        ? ('nav' as TrackPromotionPageType)
        : undefined,
      promoType: isFlyoutMenuOpen.value ? 'card' : 'interactablevideo',
      position: 1,
      creativeNameSb: props.blok.creative_name || '',
    })
  }
  emit('click', event)
}

// FIXME: Enable when we fix the impact of intersection observer on the video dimensions.
// const handleVisibility = () => {
//   legacy tracking removed
//     'view_promotion',
//     {
//       creative_slot: props.blok.creative_slot || '',
//       creative_name: props.blok.creative_name || '',
//       promotion_name: props.promotionName || 'interactable_video',
//       promotion_id: props.blok.promotion_id || '1',
//       location_id: props.blok.location_id || '',
//       ...(isFlyoutMenuOpen.value && {
//         promotion_id: flyoutMenuCategory.value?.name,
//       }),
//     },
//     {
//       url: toLocalePath(
//         ctaLink.value?.ctaLink?.cached_url || '',
//       ).toString(),
//     },
//   )
// }

const showLink = computed(() => {
  const cachedUrl = props.blok.link?.cached_url

  if (cachedUrl) {
    const parts = cachedUrl.split('/').filter(Boolean)
    const lastSegment = parts[parts.length - 1]
    if (!lastSegment) {
      return 0
    }

    if (locales.find((locale) => locale.code === lastSegment)) {
      return 0
    }

    return lastSegment.length || 0
  }

  return props.blok.product_id?.length || props.blok.order_number?.length
})
</script>
